import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { Category } from '../types/bookmarks';
import { NoteCategory, Note } from '../types/notes';
import { useCacheStore } from '../hooks/data/useCacheStore';
import { useWebAuthn } from './WebAuthnContext';

const LAST_OPENED_KEY = 'last_opened_category';

/**
 * Unified selection across both trees. Exactly one item is active at a time.
 * `type` drives which app renders in the body:
 *  - bookmarks/category → bookmarks body
 *  - notes/category     → notes list body
 *  - notes/note         → full-body note editor
 */
export type Selection =
  | { app: 'bookmarks'; type: 'category'; node: Category | null }
  | { app: 'notes'; type: 'category'; node: NoteCategory | null }
  | { app: 'notes'; type: 'note'; node: Note };

/** Persisted last-opened pointer (JSON in localStorage). */
type LastOpened =
  | { app: 'bookmarks'; type: 'category'; id: string }
  | { app: 'notes'; type: 'category'; id: string }
  | { app: 'notes'; type: 'note'; id: string };

interface CategoryContextType {
  /** Unified selection across both trees (notes + bookmarks). */
  selection: Selection;
  /** Which app the current selection belongs to. */
  activeApp: 'bookmarks' | 'notes';

  // --- Bookmark-scoped API (unchanged for existing callers) ---
  selectedCategory: Category | null;
  ancestors: Category[];
  selectCategory: (category: Category | null) => void;
  navigateToAncestor: (category: Category) => void;
  /**
   * Navigate to a bookmark's category AND flag it for highlight/scroll.
   * Sets both atomically so a category change can't clear the highlight.
   */
  navigateToBookmark: (category: Category | null, bookmarkId: string) => void;
  highlightedBookmarkId: string | null;
  setHighlightedBookmarkId: (id: string | null) => void;

  // --- Notes-scoped API ---
  selectedNoteCategory: NoteCategory | null;
  selectedNote: Note | null;
  noteAncestors: NoteCategory[];
  selectNoteCategory: (category: NoteCategory | null) => void;
  /** Open a note. `searchTerm` (from a search result) is surfaced to the editor
   * so it can highlight + scroll to the match. */
  selectNote: (note: Note, searchTerm?: string) => void;
  /** Search term to seed the open note's in-editor find, or null. */
  pendingNoteSearch: string | null;

  // Trash view (notes). When true, the body shows the Trash instead of a
  // category/note. Opening Trash clears the note selection.
  notesTrashOpen: boolean;
  openNotesTrash: () => void;
  closeNotesTrash: () => void;
}

const CategoryContext = createContext<CategoryContextType | undefined>(
  undefined,
);

type CategoryLike = {
  category_id: string;
  parent_id: string | null;
};

/**
 * Computes the ancestor chain for a given category by walking parent_id up the tree.
 * Returns ancestors ordered from root to immediate parent (top-down for breadcrumbs).
 * Generic over bookmark and note categories (same shape).
 */
function computeAncestors<T extends CategoryLike>(
  category: T | null,
  allCategories: T[],
): T[] {
  if (!category || !category.parent_id) {
    return [];
  }

  const ancestors: T[] = [];
  let currentParentId: string | null = category.parent_id;

  while (currentParentId !== null) {
    const parentId = currentParentId;
    const parent: T | undefined = allCategories.find(
      (c): boolean => c.category_id === parentId,
    );
    if (!parent) {
      break;
    }
    ancestors.unshift(parent);
    currentParentId = parent.parent_id;
  }

  return ancestors;
}

/**
 * Safely persists the last-opened pointer to localStorage as JSON.
 * Fails silently if localStorage is unavailable or quota exceeded.
 */
function persistLastOpened(pointer: LastOpened | null): void {
  try {
    if (pointer === null) {
      localStorage.removeItem(LAST_OPENED_KEY);
    } else {
      localStorage.setItem(LAST_OPENED_KEY, JSON.stringify(pointer));
    }
  } catch {
    // Requirement 17.1: continue functioning normally without persistence
  }
}

/**
 * Reads the last-opened pointer. Backward compatible: a bare (non-JSON) string
 * is treated as a legacy bookmark-category id.
 */
function readLastOpened(): LastOpened | null {
  try {
    const raw = localStorage.getItem(LAST_OPENED_KEY);
    if (raw === null) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && 'app' in parsed) {
        return parsed as LastOpened;
      }
    } catch {
      // not JSON — fall through to legacy handling
    }
    // Legacy format: bare bookmark category id string.
    return { app: 'bookmarks', type: 'category', id: raw };
  } catch {
    return null;
  }
}

/**
 * Removes the last_opened_category key from localStorage.
 */
function clearLastOpened(): void {
  try {
    localStorage.removeItem(LAST_OPENED_KEY);
  } catch {
    // Nothing we can do if removeItem fails
  }
}

export const CategoryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [ancestors, setAncestors] = useState<Category[]>([]);
  const [highlightedBookmarkId, setHighlightedBookmarkId] = useState<
    string | null
  >(null);

  // Notes selection (parallel to bookmarks; only one app is active at a time)
  const [selectedNoteCategory, setSelectedNoteCategory] =
    useState<NoteCategory | null>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [noteAncestors, setNoteAncestors] = useState<NoteCategory[]>([]);
  const [activeApp, setActiveApp] = useState<'bookmarks' | 'notes'>(
    'bookmarks',
  );
  const [notesTrashOpen, setNotesTrashOpen] = useState(false);
  const [pendingNoteSearch, setPendingNoteSearch] = useState<string | null>(
    null,
  );

  const { user } = useWebAuthn();
  const cacheStore = useCacheStore();

  // Restore last-opened selection on app load with valid session
  useEffect(() => {
    if (!user) {
      // User logged out: clear all selection state + localStorage (Req 17.4)
      setSelectedCategory(null);
      setAncestors([]);
      setSelectedNoteCategory(null);
      setSelectedNote(null);
      setNoteAncestors([]);
      setActiveApp('bookmarks');
      setNotesTrashOpen(false);
      clearLastOpened();
      return;
    }

    const pointer = readLastOpened();
    if (!pointer) {
      return;
    }

    if (pointer.app === 'bookmarks') {
      const categories = cacheStore.getCategories();
      if (!categories) return;
      const restored = categories.find((c) => c.category_id === pointer.id);
      if (restored) {
        setActiveApp('bookmarks');
        setSelectedCategory(restored);
        setAncestors(computeAncestors(restored, categories));
      } else {
        clearLastOpened();
      }
      return;
    }

    // Notes
    const noteCategories = cacheStore.getNoteCategories();
    if (pointer.type === 'category') {
      if (!noteCategories) return;
      const restored = noteCategories.find((c) => c.category_id === pointer.id);
      if (restored) {
        setActiveApp('notes');
        setSelectedNoteCategory(restored);
        setNoteAncestors(computeAncestors(restored, noteCategories));
      } else {
        clearLastOpened();
      }
    } else {
      // pointer.type === 'note'
      const notes = cacheStore.getNotes();
      const restored = notes?.find((n) => n.note_id === pointer.id);
      if (restored) {
        setActiveApp('notes');
        setSelectedNote(restored);
        const parent =
          restored.category_id && noteCategories
            ? noteCategories.find(
                (c) => c.category_id === restored.category_id,
              ) ?? null
            : null;
        setSelectedNoteCategory(parent);
        setNoteAncestors(
          parent && noteCategories
            ? [...computeAncestors(parent, noteCategories), parent]
            : [],
        );
      } else {
        clearLastOpened();
      }
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Clears the notes selection (used when switching to the bookmarks app). */
  const clearNotesSelection = useCallback(() => {
    setSelectedNoteCategory(null);
    setSelectedNote(null);
    setNoteAncestors([]);
  }, []);

  /** Clears the bookmarks selection (used when switching to the notes app). */
  const clearBookmarksSelection = useCallback(() => {
    setSelectedCategory(null);
    setAncestors([]);
    setHighlightedBookmarkId(null);
  }, []);

  // Core bookmark navigation: set selected category, compute ancestors, persist.
  const applyCategory = useCallback(
    (category: Category | null) => {
      setActiveApp('bookmarks');
      setNotesTrashOpen(false);
      clearNotesSelection();
      setSelectedCategory(category);

      if (category === null) {
        setAncestors([]);
        persistLastOpened(null);
        return;
      }

      const categories = cacheStore.getCategories();
      const computedAncestors = categories
        ? computeAncestors(category, categories)
        : [];
      setAncestors(computedAncestors);

      persistLastOpened({
        app: 'bookmarks',
        type: 'category',
        id: category.category_id,
      });
    },
    [cacheStore, clearNotesSelection],
  );

  const selectCategory = useCallback(
    (category: Category | null) => {
      setHighlightedBookmarkId(null);
      applyCategory(category);
    },
    [applyCategory],
  );

  const navigateToAncestor = useCallback(
    (category: Category) => {
      setHighlightedBookmarkId(null);
      applyCategory(category);
    },
    [applyCategory],
  );

  const navigateToBookmark = useCallback(
    (category: Category | null, bookmarkId: string) => {
      applyCategory(category);
      setHighlightedBookmarkId(bookmarkId);
    },
    [applyCategory],
  );

  // Notes navigation
  const selectNoteCategory = useCallback(
    (category: NoteCategory | null) => {
      setActiveApp('notes');
      setNotesTrashOpen(false);
      clearBookmarksSelection();
      setSelectedNote(null);
      setSelectedNoteCategory(category);

      if (category === null) {
        setNoteAncestors([]);
        persistLastOpened(null);
        return;
      }

      const noteCategories = cacheStore.getNoteCategories();
      setNoteAncestors(
        noteCategories ? computeAncestors(category, noteCategories) : [],
      );
      persistLastOpened({
        app: 'notes',
        type: 'category',
        id: category.category_id,
      });
    },
    [cacheStore, clearBookmarksSelection],
  );

  const selectNote = useCallback(
    (note: Note, searchTerm?: string) => {
      setActiveApp('notes');
      setNotesTrashOpen(false);
      clearBookmarksSelection();
      setPendingNoteSearch(searchTerm ?? null);
      setSelectedNote(note);

      const noteCategories = cacheStore.getNoteCategories();
      const parent =
        note.category_id && noteCategories
          ? noteCategories.find((c) => c.category_id === note.category_id) ??
            null
          : null;
      setSelectedNoteCategory(parent);
      setNoteAncestors(
        parent && noteCategories
          ? [...computeAncestors(parent, noteCategories), parent]
          : [],
      );

      persistLastOpened({ app: 'notes', type: 'note', id: note.note_id });
    },
    [cacheStore, clearBookmarksSelection],
  );

  const openNotesTrash = useCallback(() => {
    setActiveApp('notes');
    clearBookmarksSelection();
    setSelectedNote(null);
    setSelectedNoteCategory(null);
    setNoteAncestors([]);
    setNotesTrashOpen(true);
  }, [clearBookmarksSelection]);

  const closeNotesTrash = useCallback(() => {
    setNotesTrashOpen(false);
  }, []);

  // Derive the unified selection object from the active app + state.
  const selection: Selection =
    activeApp === 'notes'
      ? selectedNote !== null
        ? { app: 'notes', type: 'note', node: selectedNote }
        : { app: 'notes', type: 'category', node: selectedNoteCategory }
      : { app: 'bookmarks', type: 'category', node: selectedCategory };

  return (
    <CategoryContext.Provider
      value={{
        selection,
        activeApp,
        selectedCategory,
        ancestors,
        selectCategory,
        navigateToAncestor,
        navigateToBookmark,
        highlightedBookmarkId,
        setHighlightedBookmarkId,
        selectedNoteCategory,
        selectedNote,
        noteAncestors,
        selectNoteCategory,
        selectNote,
        pendingNoteSearch,
        notesTrashOpen,
        openNotesTrash,
        closeNotesTrash,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategoryContext = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error(
      'useCategoryContext must be used within a CategoryProvider',
    );
  }
  return context;
};
